import { useState, useEffect, useRef } from "react";
import { useRule } from "../CreateRuleNew/RuleReducer";
import Button2 from "../../../Button/Button2";
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { Calendar } from "primereact/calendar";
import { Divider } from "primereact/divider";
import { Dropdown } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import {
    GoogleMap,
    useLoadScript,
    Marker,
    DrawingManager,
    Circle,
} from "@react-google-maps/api";
import classes from "./RuleWizard.module.css";
import Button3 from "../../../Button/Button3";
import { Dialog } from "primereact/dialog";
import { GATEWAY_EVENTS } from "./WizardStepTrigger";

const Days = ["Mon", "Tues", "Wed", "Thur", "Fri", "Sat", "Sun"];

const GEO_LIBRARIES = ["drawing"];



const GeofenceLocation = ({ onNext, onBack }) => {
    const [center, setCenter] = useState({ lat: 51.5074, lng: -0.1278 });
    const [circleData, setCircleData] = useState(null);
    const mapRef = useRef(null);

    const { isLoaded } = useLoadScript({
        googleMapsApiKey: "AIzaSyCLdJH2r1Lue1d_rZEDRQv1EYCShhRxETY",
        libraries: GEO_LIBRARIES,
    });

    useEffect(() => {
        navigator.geolocation.getCurrentPosition(
            (pos) => {
                const loc = { lat: pos.coords.latitude, lng: pos.coords.longitude };
                setCenter(loc);
                setCircleData({ ...loc, radius: 50 });
                if (mapRef.current) mapRef.current.panTo(loc);
            },
            () => {}
        );
    }, []);

    const onCircleComplete = (circle) => {
        circle.setMap(null);
        setCircleData({
            lat: circle.getCenter().lat(),
            lng: circle.getCenter().lng(),
            radius: circle.getRadius(),
        });
    };

    if (!isLoaded) return <div style={{ padding: "20px", color: "#9e9e9e" }}>Loading map...</div>;

    return (
        <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: "14px", minHeight: 0 }}>
            <GoogleMap
                center={center}
                zoom={17}
                onLoad={m => (mapRef.current = m)}
                mapContainerStyle={{ width: "100%", flex: 1, borderRadius: "8px", minHeight: "300px" }}
            >
                <Marker position={center} />
                <DrawingManager
                    onCircleComplete={onCircleComplete}
                    options={{
                        drawingControl: true,
                        drawingControlOptions: { drawingModes: ["circle"] },
                        circleOptions: { fillColor: "#FF0000", fillOpacity: 0.2, strokeWeight: 2, editable: true },
                    }}
                />
                {circleData && (
                    <Circle center={{ lat: circleData.lat, lng: circleData.lng }} radius={circleData.radius} />
                )}
            </GoogleMap>
            <div className={classes.navRow} style={{ marginTop: 0 }}>
                <Button label="Back" icon="pi pi-arrow-left" severity="secondary" outlined onClick={onBack} />
                <Button
                    label="Next"
                    icon="pi pi-arrow-right"
                    iconPos="right"
                    severity="success"
                    disabled={!circleData}
                    onClick={onNext}
                />
            </div>
        </div>
    );
};

const TIMEFRAME_UNITS = [
    { label: "Seconds", value: 1000 },
    { label: "Minutes", value: 60000 },
    { label: "Hours",   value: 3600000 },
];

const WizardStepConditions = ({ pins, smartLocks, onNext, onBack }) => {
    const { state, dispatch } = useRule();

    const formatTimeframeDuration = (ms) => {
        if (ms < 1000) return `${ms}ms`;
        const seconds = ms / 1000;
        if (seconds <= 60) return `${seconds} seconds`;
        return `${seconds / 60} minutes`;
    };

    const GATEWAY_EVENT_PHRASES = {
        unlocked: "a padlock unlock",
        opened: "a padlock open",
        locked: "a padlock close",
        invalidPin: "an invalid pin attempt",
    };

    const conditionChipLabel = (cond) => {
        if (cond.condition_type === "GATEWAY_EVENT") {
            const phrase = GATEWAY_EVENT_PHRASES[cond.gateway_event_subtype] || cond.pin_name;
            return `${phrase} is reported by the gateway`;
        }
        return `${state.isTimeTrigger ? "If" : "When"} ${cond.pin_name ?? `Switch ${cond.pin}`} ${conditionPinStateLabel(cond)}`;
    };

    const conditionPinStateLabel = (cond) => {
        const isLock = cond.is_lock ?? state.isLock;
        const isAbsence = cond.is_absence_condition;
        const isDeadline = cond.is_deadline_condition;
        const onLabel  = isLock ? "closed" : (cond.pin_status_on  ?? state.selectedRulePin.pinStatusOn  ?? "armed");
        const offLabel = isLock ? "open"   : (cond.pin_status_off ?? state.selectedRulePin.pinStatusOff ?? "disarmed");
        const timeSuffix = state.isTimeTrigger && state.triggerTime ? ` at ${state.triggerTime.slice(0, 5)}` : "";
        const absenceTimeSuffix = ` after ${state.timeframe_window_ms > 0 ? formatTimeframeDuration(state.timeframe_window_ms) : "... time"}`;
        switch (cond.pin_state) {
            case "ARMED":    return isAbsence ? `is still in the ${offLabel} status${absenceTimeSuffix}` : isDeadline ? `is already ${onLabel}` : state.isTimeTrigger ? `is still ${onLabel}${timeSuffix}` : `changes status to ${onLabel}`;
            case "DISARMED": return isAbsence ? `is still in the ${onLabel} status${absenceTimeSuffix}` : isDeadline ? `is already ${offLabel}` : state.isTimeTrigger ? `is still ${offLabel}${timeSuffix}` : `changes status to ${offLabel}`;
            case "ANY":      return state.isTimeTrigger ? `is ${onLabel} or ${offLabel} (toggled)${timeSuffix}` : `changes status to ${onLabel} or ${offLabel} (toggled)`;
            case "PULSE":    return `is pulsed${timeSuffix}`;
            default:         return `is ${cond.pin_state || "..."}${timeSuffix}`;
        }
    };

    const currentCondition = state.conditions[state.conditionEditIndex];

    const [pinState, setPinState] = useState(currentCondition?.pin_state || "");
    const [isAbsenceCondition, setIsAbsenceCondition] = useState(currentCondition?.is_absence_condition || false);
    const [isDeadlineCondition, setIsDeadlineCondition] = useState(currentCondition?.is_deadline_condition || false);
    const [deadlineDescriptionVisible, setDeadlineDescriptionVisible] = useState(false);
    const [disabled, setDisabled] = useState(!currentCondition?.pin_state || state.isPulse ? false : true);
    // const [showTimeframe, setShowTimeframe] = useState(false);
    // const [timeframeValue, setTimeframeValue] = useState("");
    // const [timeframeUnit, setTimeframeUnit] = useState(60000);
    const [showPinSelector, setShowPinSelector] = useState(false);
    const [showConditionSettings, setShowConditionSettings] = useState(false);
    const [pendingLogicOperator, setPendingLogicOperator] = useState(state.conditions_logic_operator || "AND");
    const [pendingSequential, setPendingSequential] = useState(state.evaluation_mode === "SEQUENCE" ? "YES" : "NO");
    const [conditionDescriptionVisible, setConditionDescriptionVisible] = useState(false)

    // Gateway-primary rules: "add another condition" flow, relocated here from the trigger step
    const gatewayConditions = state.conditions.filter(c => c.condition_type === "GATEWAY_EVENT");
    const switchConditions = state.conditions.filter(c => c.condition_type !== "GATEWAY_EVENT");
    const isGatewayPrimary = state.triggerType === "GATEWAY";
    const hasSwitchExtra = switchConditions.length > 0;
    const hasAnyExtra = hasSwitchExtra || state.isTimeTrigger;
    const showGatewayChooser = isGatewayPrimary && !hasSwitchExtra;
    // Once a status is chosen for the current condition, SET_INPUT_TYPE moves off -1 —
    // that's the signal to move from "choose what it does" to the chip + add-another-condition summary.
    // Pulse pins have nothing to choose (there's only one possible state), so they skip straight to the summary.
    const showConditionEditor = !showGatewayChooser && state.inputType === -1 && !state.isPulse;
    const showConditionSummary = !showGatewayChooser && !showConditionEditor;
    const [showGatewayExtra, setShowGatewayExtra] = useState(false);
    const [gatewayAddMode, setGatewayAddMode] = useState(null); // null | "GATEWAY" | "SWITCH" | "TIME"
    const [gatewayTriggerTime, setGatewayTriggerTime] = useState(null);

    const convertSelectionToSubType = (selection) => {
        switch (selection) {
            case 0: return "unlocked";
            case 1: return "opened";
            case 2: return "locked";
            case 3: return "invalidPIN";
            default: return "";
        }
    };

    const availableGatewayEvents = GATEWAY_EVENTS
        .map((label, idx) => ({ label, idx }))
        .filter(({ idx }) => !gatewayConditions.some(c => c.gateway_event_subtype === convertSelectionToSubType(idx)));

    const handleAddGatewayEvent = (trigger) => {
        const condition = {
            condition_type: "GATEWAY_EVENT",
            pin_name: GATEWAY_EVENTS[trigger],
            gateway_event_family: "lockEvent",
            gateway_event_subtype: convertSelectionToSubType(trigger),
            time_condition: "ANYTIME",
            days_of_week: ["0", "1", "2", "3", "4", "5", "6"],
            is_absence_condition: false,
        };
        dispatch({ type: "SET_ADV_RULE_CONDITION", payload: [...state.conditions, condition] });
        setGatewayAddMode(null);
        setShowGatewayExtra(false);
    };

    const handleAddSwitchExtra = (pin) => {
        handleAddAnotherPin(pin);
        setGatewayAddMode(null);
        setShowGatewayExtra(false);
    };

    const handleGatewayTime = (value) => {
        setGatewayTriggerTime(value);
        if (!value) {
            dispatch({ type: "SET_IS_TIME_TRIGGER", payload: false });
            dispatch({ type: "SET_TRIGGER_TIME", payload: null });
            return;
        }
        dispatch({ type: "SET_IS_TIME_TRIGGER", payload: true });
        dispatch({
            type: "SET_TRIGGER_TIME",
            payload: `${String(value.getHours()).padStart(2, "0")}:${String(value.getMinutes()).padStart(2, "0")}`,
        });
        dispatch({ type: "SET_TRIGGER_TIMEZONE", payload: Intl.DateTimeFormat().resolvedOptions().timeZone });
        setGatewayAddMode(null);
        setShowGatewayExtra(false);
    };

    const toggleGatewayDay = (dayIndex) => {
        const next = state.days_of_week.includes(dayIndex)
            ? state.days_of_week.filter(d => d !== dayIndex)
            : [...state.days_of_week, dayIndex];
        dispatch({ type: "SET_EXECUTION_DAYS", payload: next });
    };

    // Re-init editor state when switching to a different condition
    useEffect(() => {
        const cond = state.conditions[state.conditionEditIndex];
        if (!cond) return;
        setPinState(cond.pin_state || "");
        setIsAbsenceCondition(cond.is_absence_condition || false);
        setIsDeadlineCondition(cond.is_deadline_condition || false);
        setDisabled(!(cond.pin_state) && !state.isPulse);
        setShowPinSelector(false);
    }, [state.conditionEditIndex]);

    // Sync local editor state into the condition object
    useEffect(() => {
        if (!pinState && !state.isPulse) return;
        const updated = state.conditions.map((cond, i) => {
            if (i !== state.conditionEditIndex) return cond;
            return {
                ...cond,
                pin_state: pinState || cond.pin_state,
                is_absence_condition: isAbsenceCondition,
                is_deadline_condition: isDeadlineCondition,
            };
        });
        dispatch({ type: "SET_ADV_RULE_CONDITION", payload: updated });
    }, [pinState, isAbsenceCondition, isDeadlineCondition]);

    const handleStateSelected = (selection) => {
        dispatch({ type: "SET_INPUT_TYPE", payload: selection });
        dispatch({ type: "SET_INPUT_TYPE_COPY", payload: selection });
        switch (selection) {
            case 0: setPinState("ARMED"); setIsAbsenceCondition(false); setIsDeadlineCondition(false); break;
            case 1: setPinState("ANY"); setIsAbsenceCondition(false); setIsDeadlineCondition(false); break;
            case 2: setPinState("DISARMED"); setIsAbsenceCondition(false); setIsDeadlineCondition(false); break;
            case 3: setPinState("DISARMED"); setIsAbsenceCondition(true); setIsDeadlineCondition(false); break;
            case 4: setPinState("ARMED"); setIsAbsenceCondition(true); setIsDeadlineCondition(false); break;
            case 5: setLockLeftOpen(); setIsAbsenceCondition(false); setIsDeadlineCondition(false); break;
            default: break;
        }
        setDisabled(false);
    };

    const setLockLeftOpen = () => {
        const currentCond = state.conditions[state.conditionEditIndex];
        const firstCondition = { ...currentCond, pin_state: "DISARMED", is_absence_condition: false, is_deadline_condition: false };
        const secondCondition = { ...currentCond, pin_state: "ARMED", is_absence_condition: true, is_deadline_condition: false };

        const updatedConditions = [
            ...state.conditions.slice(0, state.conditionEditIndex),
            firstCondition,
            ...state.conditions.slice(state.conditionEditIndex + 1),
            secondCondition,
        ];

        dispatch({ type: "SET_ADV_RULE_CONDITION", payload: updatedConditions });
        setPinState("DISARMED");
        setIsAbsenceCondition(false);
        setIsDeadlineCondition(false);
    }

    const renderStateText = (pinState) => {
        const newState = pinState
            switch (pinState) {
                case "armed": 
                    newState = state.isLock ? "closed" : state.selectedRulePin.pinStatusOn
                break;
                case "disarmed": 
                    newState = state.isLock ? "open" : state.selectedRulePin.pinStatusOff
                break;
                case "armed or disarmed":
                    newState = state.isLock ? "open or closed" : `${state.selectedRulePin.pinStatusOff} or ${state.selectedRulePin.pinStatusOn}`
                break;
            }

        return newState;
    }

    const renderConditionButton = (idx, label) => (
        <Button3
            key={idx}
            style={{
                minWidth: "280px", minHeight: "40px",
                backgroundColor: state.inputType === idx ? "var(--Primary-Color)" : undefined,
                color: state.inputType === idx ? "white" : undefined,
                border: state.inputType === idx ? "2px solid lightblue" : undefined,
                whiteSpace: "normal",
            }}
            onClick={() => handleStateSelected(idx)}
        >
            {label}
        </Button3>
    );

    const conditionGroupCaptionStyle = { color: "#6e6e6e", fontSize: "13px", margin: "0 0 4px 0", fontWeight: 600 };

    const isInputPin = (pin) => pin.type === "INPUT" || pin.type === "VIRTUAL_INPUT";

    const renderPinPicker = (pinList, onPick) => {
        const outputPins = pinList.filter(pin => !isInputPin(pin));
        const inputPins = pinList.filter(isInputPin);
        return (
            <>
                {outputPins.length > 0 && (
                    <div style={{ marginBottom: inputPins.length > 0 ? "10px" : 0 }}>
                        <p style={{ color: "#9e9e9e", fontSize: "11px", fontWeight: 700, textTransform: "uppercase", margin: "0 0 6px 0" }}>Outputs</p>
                        <div className={classes.pinGrid}>
                            {outputPins.map((pin, i) => (
                                <Button3
                                    key={i}
                                    style={{ minWidth: "130px", minHeight: "36px", fontSize: "13px" }}
                                    onClick={() => onPick(pin)}
                                >
                                    {pin.pinName}
                                </Button3>
                            ))}
                        </div>
                    </div>
                )}
                {inputPins.length > 0 && (
                    <div>
                        <p style={{ color: "#9e9e9e", fontSize: "11px", fontWeight: 700, textTransform: "uppercase", margin: "0 0 6px 0" }}>Inputs</p>
                        <div className={classes.pinGrid}>
                            {inputPins.map((pin, i) => (
                                <Button3
                                    key={i}
                                    style={{ minWidth: "130px", minHeight: "36px", fontSize: "13px" }}
                                    onClick={() => onPick(pin)}
                                >
                                    {pin.pinName}
                                </Button3>
                            ))}
                        </div>
                    </div>
                )}
            </>
        );
    };

    const openConditionSettings = () => {
        setPendingLogicOperator(state.conditions_logic_operator || "AND");
        setPendingSequential(state.evaluation_mode === "SEQUENCE" ? "YES" : "NO");
        setShowConditionSettings(true);
    };

    const confirmConditionSettings = () => {
        dispatch({ type: "SET_CONDITIONS_LOGIC_OPERATOR", payload: pendingLogicOperator });
        dispatch({ type: "SET_IS_SEQUENTIAL", payload: pendingSequential });
        dispatch({
            type: "SET_ADV_RULE_CONDITION",
            payload: pendingSequential === "YES"
                ? state.conditions.map((cond, i) => ({ ...cond, sequence_order: i + 1 }))
                : state.conditions.map(({ sequence_order, ...rest }) => rest),
        });
        setShowConditionSettings(false);
        setShowPinSelector(true);
    };

    const handleAddAnotherPin = (pin) => {
        const newIndex = state.conditions.length;
        const isSequential = state.evaluation_mode === "SEQUENCE";
        const isLock = smartLocks.some(l => parseInt(l.outputPin) === pin.Pin);
        const condition = {
            condition_type: "STATE_CHECK",
            pin: pin.Pin,
            pin_name: pin.pinName,
            pin_state: pin.type === "PULSE" || pin.type === "VIRTUAL_PULSE" ? "PULSE" : pin.pin_state,
            trigger_method: pin.type === "PULSE" || pin.type === "VIRTUAL_PULSE" ? "PULSE" : "LATCH",
            trigger_method_type: "ANY",
            time_condition: "ANYTIME",
            days_of_week: ["0","1","2","3","4","5","6"],
            is_absence_condition: false,
            is_deadline_condition: false,
            is_lock: isLock,
            pin_status_on: pin.pinStatusOn,
            pin_status_off: pin.pinStatusOff,
            ...(isSequential && { sequence_order: state.conditions.length + 1 }),
        };
        dispatch({ type: "SET_ADV_RULE_CONDITION", payload: [...state.conditions, condition] });
        dispatch({ type: "SET_CONDITION_EDIT_INDEX", payload: newIndex });
        dispatch({ type: "SET_SELECTED_PIN", payload: pin });
        dispatch({ type: "SET_PIN_NUMBER", payload: pin.Pin });
        dispatch({ type: "SET_IS_LOCK", payload: isLock });
        dispatch({ type: "SET_PIN_STATUS_ON", payload: pin.pinStatusOn });
        dispatch({ type: "SET_PIN_STATUS_OFF", payload: pin.pinStatusOff });
        dispatch({ type: "SET_PIN_NAME", payload: pin.pinName });
        dispatch({ type: "SET_IS_PULSE", payload: pin.type === "PULSE" || pin.type === "VIRTUAL_PULSE" });
        dispatch({ type: "SET_INPUT_TYPE", payload: -1 });
        dispatch({ type: "SET_INPUT_TYPE_COPY", payload: -1 });
    };

    if (state.isGeofence) {
        return (
            <>
                <div className={classes.contentHeader}>
                    <h2 className={classes.contentTitle}>Set geofence location</h2>
                    <p className={classes.contentSubtitle}>Draw a circle on the map to define the geofence zone.</p>
                </div>
                <GeofenceLocation onNext={onNext} onBack={onBack} />
            </>
        );
    }

    const canProceed = !disabled || state.isPulse;

    return (
        <>

        <Dialog
            visible={showConditionSettings}
            onHide={() => setShowConditionSettings(false)}
            header="Add another condition"
            modal
            style={{ width: "460px" }}
            footer={
                <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
                    <Button label="Cancel" severity="secondary" outlined onClick={() => setShowConditionSettings(false)} />
                    <Button label="Continue" severity="success" onClick={confirmConditionSettings} />
                </div>
            }
        >
            <div style={{ display: "flex", flexDirection: "column", gap: "8px", marginBottom: "16px" }}>
                <p style={{ margin: 0, color: "#6e6e6e", fontSize: "14px", fontWeight: 600 }}>
                    How should the conditions be combined?
                </p>
                <div style={{ display: "flex", gap: "8px" }}>
                    <Button3
                        style={{
                            flex: 1, minHeight: "40px",
                            backgroundColor: pendingLogicOperator === "AND" ? "var(--Primary-Color)" : undefined,
                            color: pendingLogicOperator === "AND" ? "white" : undefined,
                        }}
                        onClick={() => setPendingLogicOperator("AND")}
                    >
                        AND — all must be true
                    </Button3>
                    <Button3
                        style={{
                            flex: 1, minHeight: "40px",
                            backgroundColor: pendingLogicOperator === "OR" ? "var(--Primary-Color)" : undefined,
                            color: pendingLogicOperator === "OR" ? "white" : undefined,
                        }}
                        onClick={() => setPendingLogicOperator("OR")}
                    >
                        OR — any can be true
                    </Button3>
                </div>
            </div>

            <Divider style={{ margin: "0 0 16px 0" }} />

            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                <p style={{ margin: 0, color: "#6e6e6e", fontSize: "14px", fontWeight: 600 }}>
                    Does execution order matter?
                </p>
                <div style={{ display: "flex", gap: "8px" }}>
                    <Button3
                        style={{
                            flex: 1, minHeight: "40px",
                            backgroundColor: pendingSequential === "NO" ? "var(--Primary-Color)" : undefined,
                            color: pendingSequential === "NO" ? "white" : undefined,
                        }}
                        onClick={() => setPendingSequential("NO")}
                    >
                        No, any order
                    </Button3>
                    <Button3
                        style={{
                            flex: 1, minHeight: "40px",
                            backgroundColor: pendingSequential === "YES" ? "var(--Primary-Color)" : undefined,
                            color: pendingSequential === "YES" ? "white" : undefined,
                        }}
                        onClick={() => setPendingSequential("YES")}
                    >
                        Yes, order matters
                    </Button3>
                </div>
            </div>
        </Dialog>
            <div className={classes.contentHeader}>
                <h2 className={classes.contentTitle}>
                    {showConditionEditor ? "Choose what state it has to switch to" : "When does it trigger?"}
                </h2>
                <p className={classes.contentSubtitle}>
                    {showConditionEditor
                        ? "Choose the status change this condition looks for."
                        : "Configure the condition(s) that must be met for the rule to fire."}
                </p>
            </div>

            <div style={{ flex: 1, overflowY: "auto", display: "flex", flexDirection: "column", gap: "14px" }}>

                {/* Conditions chips + AND/OR + timeframe */}
                {!showConditionEditor && (() => {
                    const triggerConditions = state.conditions.filter(c => !c.is_deadline_condition);
                    const deadlineConditions = state.conditions.filter(c => c.is_deadline_condition);
                    return (
                        <>
                            {triggerConditions.length > 0 && (
                                <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                                    <div className={classes.conditionChips}>
                                        {triggerConditions.map((cond, i) => (
                                            <span key={i} style={{ display: "contents" }}>
                                                <span
                                                    className={`${classes.chip}`}
                                                    style={{ cursor: "pointer" }}
                                                >
                                                {conditionChipLabel(cond)}
                                                </span>
                                                {i < triggerConditions.length - 1 && (
                                                    <span className={classes.sidebarChipThen}>
                                                        {state.evaluation_mode === "SEQUENCE" ? "THEN" : state.conditions_logic_operator}
                                                    </span>
                                                )}
                                            </span>
                                        ))}
                                        {isGatewayPrimary && state.isTimeTrigger && (
                                            <span className={classes.chip}>{`Time · ${state.triggerTime?.slice(0, 5) ?? "..."}`}</span>
                                        )}
                                    </div>
                                </div>
                            )}
                            {deadlineConditions.length > 0 && (
                                <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                                    <p style={{ margin: 0, color: "#9e9e9e", fontSize: "11px", fontWeight: 700, textTransform: "uppercase" }}>
                                        {`Status checks (${state.deadline_logic_operator})`}
                                    </p>
                                    <div className={classes.conditionChips}>
                                        {deadlineConditions.map((cond, i) => (
                                            <span key={i} style={{ display: "contents" }}>
                                                <span className={`${classes.chip}`}>
                                                    {conditionChipLabel(cond)}
                                                </span>
                                                {i < deadlineConditions.length - 1 && (
                                                    <span className={classes.sidebarChipThen}>
                                                        {state.deadline_logic_operator}
                                                    </span>
                                                )}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </>
                    );
                })()}

                <Divider style={{ margin: "0" }} />

                {showGatewayChooser && (
                    <div>
                        {!showGatewayExtra ? (
                            <Button
                                label="Add another condition"
                                icon="pi pi-plus"
                                size="small"
                                severity="info"
                                rounded
                                outlined
                                onClick={() => { setShowGatewayExtra(true); setGatewayAddMode(null); }}
                            />
                        ) : gatewayAddMode === null ? (
                            <div style={{ display: "flex", flexDirection: "column", gap: "8px", alignItems: "flex-start" }}>
                                <p className={classes.contentSubtitle}>What kind of condition do you want to add?</p>
                                <Button3
                                    style={{ minWidth: "220px", minHeight: "38px" }}
                                    disabled={availableGatewayEvents.length === 0}
                                    onClick={() => setGatewayAddMode("GATEWAY")}
                                >
                                    Another gateway event
                                </Button3>
                                <Button3 style={{ minWidth: "220px", minHeight: "38px" }} onClick={() => setGatewayAddMode("SWITCH")}>
                                    An output or input changes state
                                </Button3>
                                {!state.isTimeTrigger && (
                                    <Button3 style={{ minWidth: "220px", minHeight: "38px" }} onClick={() => setGatewayAddMode("TIME")}>
                                        Time of day
                                    </Button3>
                                )}
                                <Button label="Cancel" icon="pi pi-times" size="small" severity="secondary" text onClick={() => setShowGatewayExtra(false)} />
                            </div>
                        ) : gatewayAddMode === "GATEWAY" ? (
                            <>
                                <p className={classes.contentSubtitle}>Choose another gateway event</p>
                                {availableGatewayEvents.map(({ label, idx }) => (
                                    <Button3
                                        key={label}
                                        style={{ minWidth: "50px", minHeight: "30px", fontSize: "12px", marginTop: "8px", marginRight: "8px" }}
                                        onClick={() => handleAddGatewayEvent(idx)}
                                    >
                                        {label}
                                    </Button3>
                                ))}
                                <br />
                                <Button label="Back" size="small" severity="secondary" text style={{ marginTop: "8px" }} onClick={() => setGatewayAddMode(null)} />
                            </>
                        ) : gatewayAddMode === "SWITCH" ? (
                            <>
                                <p className={classes.contentSubtitle}>Choose a switch for the extra condition</p>
                                <br/>
                                {renderPinPicker(pins, handleAddSwitchExtra)}
                                <Button label="Back" size="small" severity="secondary" text style={{ marginTop: "8px" }} onClick={() => setGatewayAddMode(null)} />
                            </>
                        ) : gatewayAddMode === "TIME" ? (
                            <>
                                <p className={classes.contentSubtitle}>Choose a time for this rule to trigger</p>
                                <br/>
                                <div className={classes.pinGrid}>
                                    <Calendar
                                        placeholder={"Time"}
                                        value={gatewayTriggerTime}
                                        onChange={(e) => handleGatewayTime(e.value)}
                                        timeOnly
                                        appendTo="self"
                                    />
                                </div>
                                <p style={{ color: "#6e6e6e", fontSize: "13px", margin: "14px 0 6px 0" }}>Active on:</p>
                                <div style={{ display: "flex", flexWrap: "wrap", gap: "5px" }}>
                                    {["1", "2", "3", "4", "5", "6", "0"].map((d, i) => (
                                        <Button3
                                            key={i}
                                            style={{
                                                minWidth: "50px", minHeight: "30px", fontSize: "12px",
                                                backgroundColor: state.days_of_week.includes(d) ? "var(--Primary-Color)" : undefined,
                                                color: state.days_of_week.includes(d) ? "white" : undefined,
                                            }}
                                            onClick={() => toggleGatewayDay(d)}
                                        >
                                            {Days[i]}
                                        </Button3>
                                    ))}
                                </div>
                                <Button label="Back" size="small" severity="secondary" text style={{ marginTop: "8px" }} onClick={() => setGatewayAddMode(null)} />
                            </>
                        ) : null}
                    </div>
                )}

                {showConditionEditor && (
                <>
                {/* Condition editor */}

                {state.isTimeTrigger && state.triggerTime && (
                        <p style={{ margin: "4px 0 0 0", fontSize: "20px", color: "#6e6e6e" }}>
                            At <strong>{state.triggerTime.slice(0, 5)}</strong>
                        </p>
                    )}

                <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                    <h4 style={{ color: "#6e6e6e", margin: 0, fontSize: "18px", marginBottom:"10px" }}>
                        {state.isTimeTrigger ? "If" : "When"} <strong>{state.pinName}</strong>:
                    </h4>

                    {/* {state.conditionEditIndex > 0 && !state.isTimeTrigger && !state.isPulse && (
                        <div style={{ display: "flex", flexDirection: "column", gap: "2px", marginBottom: "4px" }}>
                            <div
                                style={{ display: "flex", alignItems: "center", gap: "10px", cursor: "pointer" }}
                                onClick={() => setIsDeadlineCondition(!isDeadlineCondition)}
                            >
                                <Checkbox checked={isDeadlineCondition} onChange={() => {}} />
                                <span style={{ fontSize: "14px", color: "#6e6e6e" }}>
                                    Check the current status instead of waiting for it to change
                                </span>
                            </div>
                            <p
                                style={{ color: "#4f7bff", margin: "0 0 0 30px", fontSize: "13px", fontWeight: 400, cursor: "pointer" }}
                                onClick={() => setDeadlineDescriptionVisible(true)}
                            >
                                What's this?
                            </p>
                        </div>
                    )} */}

                    <div style={{ display: "flex", flexDirection: "column", gap: "12px", alignItems: "flex-start" }}>
                        <div style={{ display: "flex", flexDirection: "column", gap: "6px", alignItems: "flex-start" }}>
                            <p style={conditionGroupCaptionStyle}>{`${state.isTimeTrigger ? "" : "Changes status"}`}</p> 
                            {renderConditionButton(0, state.isLock
                                ? (state.isTimeTrigger ? "is still closed" : "to closed")
                                : (state.isTimeTrigger ? `is still ${state.pinStatusOn}` : `to ${state.pinStatusOn}`))}
                            {!state.isTimeTrigger && renderConditionButton(1, state.isLock
                                ? "to closed or open (toggled)"
                                : `to ${state.pinStatusOn} or ${state.pinStatusOff} (toggled)`)}
                            {renderConditionButton(2, state.isLock
                                ? (state.isTimeTrigger ? "is still open" : "to open")
                                : (state.isTimeTrigger ? `is still ${state.pinStatusOff}` : `to ${state.pinStatusOff}`))}
                        </div>

                        {state.supportsAdvRules && !state.isTimeTrigger && state.conditionEditIndex > 0 && (
                            <div style={{ display: "flex", flexDirection: "column", gap: "6px", alignItems: "flex-start" }}>
                                <p style={conditionGroupCaptionStyle}>Or, is in a status</p>
                                {renderConditionButton(3, state.isLock ? "is in the open status" : `is in the ${state.pinStatusOff} status`)}
                                {renderConditionButton(4, state.isLock ? "is in the closed status" : `is in the ${state.pinStatusOn} status`)}
                            </div>
                        )}

                        {state.supportsAdvRules && (state.isLock || state.isInput) && (!state.isTimeTrigger) && (
                            <div style={{ display: "flex", flexDirection: "column", gap: "6px", alignItems: "flex-start" }}>
                                <p style={conditionGroupCaptionStyle}>Or, when it changes status and hasn't reverted in time</p>
                                {state.isLock
                                    ? renderConditionButton(5, "Has been left open")
                                    : renderConditionButton(5, `Has been ${state.pinStatusOff} and hasn't returned to ${state.pinStatusOn}`)}
                            </div>
                        )}
                    </div>

                    
                </div>
                </>
                )}

                {showConditionSummary && (
                <>
                {/* Add another condition */}
                <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                    {!showPinSelector ? (
                        <>
                         <Button
                            label="Add another condition"
                            icon="pi pi-plus"
                            size="small"
                            severity="info"
                            rounded
                            disabled={!canProceed}
                            onClick={openConditionSettings}
                        />
                        <p style={{ color: "#4f7bff", marginTop: 5, fontSize: "13px", fontWeight: 400, cursor:"pointer", display:"flex", flexDirection:"row", alignContent:"center"}} onClick={() => setConditionDescriptionVisible(true)}>What's this?</p>
                    </>    
                    ) : (
                    <div className={classes.pinSelectorPanel}>
                        <p style={{ color: "#6e6e6e", fontSize: "13px", margin: "0 0 10px 0", fontWeight: 600 }}>
                            Choose a switch for the next condition:
                        </p>
                        {renderPinPicker(pins, handleAddAnotherPin)}
                        <Button
                            label="Cancel"
                            icon="pi pi-times"
                            size="small"
                            severity="secondary"
                            text
                            style={{ marginTop: "8px" }}
                            onClick={() => setShowPinSelector(false)}
                        />
                    </div>
                    )}
                </div>
                </>
                )}
            </div>

            <div className={classes.navRow}>
                <Button label="Back" icon="pi pi-arrow-left" severity="secondary" outlined onClick={onBack} />
                {showGatewayChooser ? (
                    hasAnyExtra ? (
                        <Button
                            label="Next"
                            icon="pi pi-arrow-right"
                            iconPos="right"
                            severity="success"
                            onClick={onNext}
                        />
                    ) : (
                        <Button
                            label="Skip"
                            icon="pi pi-arrow-right"
                            iconPos="right"
                            severity="secondary"
                            outlined
                            onClick={onNext}
                        />
                    )
                ) : showConditionEditor ? (
                    <div />
                ) : (
                    <Button
                        label="Next"
                        icon="pi pi-arrow-right"
                        iconPos="right"
                        severity="success"
                        disabled={!canProceed}
                        onClick={onNext}
                    />
                )}
            </div>

             <Dialog
                header="What does adding another condition do?"
                visible={conditionDescriptionVisible}
                style={{ width: "34vw" }}
                onHide={() => setConditionDescriptionVisible(false)}
            >
                <p style={{ margin: 0, fontSize: "14px", color: "#3d3d3d", lineHeight: 1.6 }}>
                    Adding another condition turns this into a multi-condition rule. You'll be asked how the
                    conditions combine — AND means every condition must be true, OR means any one of them being
                    true is enough — and whether the order they happen in matters, e.g. does Condition 1 need to
                    happen before Condition 2, or can they happen in any order?
                </p>
                <p style={{ margin: "12px 0 0 0", fontSize: "14px", color: "#3d3d3d", lineHeight: 1.6 }}>
                    For example: If Output 1 is armed AND Output 2 is armed → send an alert. (You'll choose what
                    the rule actually does — send an alert, take a snapshot, or switch another output — in a later step.)
                </p>
                <br/>
                <p style={{ margin: "12px 0 0 0", fontSize: "14px", color: "#3d3d3d", lineHeight: 1.6 }}>
                    On the next step, you can choose a timeframe these conditions must be satisfied within. For example if you choose 30 seconds, both condition 1 and condition 2 must be met within 30 seconds otherwise the rule will not execute
                </p>
                <br />
                <div style={{ display: "flex", justifyContent: "flex-end" }}>
                    <Button label="Got it" severity="success" onClick={() => setConditionDescriptionVisible(false)} />
                </div>
            </Dialog>

            <Dialog
                header="What does checking the current status do?"
                visible={deadlineDescriptionVisible}
                style={{ width: "34vw" }}
                onHide={() => setDeadlineDescriptionVisible(false)}
            >
                <p style={{ margin: 0, fontSize: "14px", color: "#3d3d3d", lineHeight: 1.6 }}>
                    By default, every condition waits for its switch to actually change to the selected status
                    before it counts as met — the same as the trigger. Turning this on changes that: instead of
                    waiting for a change, the rule simply checks whatever status this switch is already in the
                    moment the trigger fires (or within the timeframe window), without waiting for it to move.
                </p>
                <p style={{ margin: "12px 0 0 0", fontSize: "14px", color: "#3d3d3d", lineHeight: 1.6 }}>
                    For example: Output 1 changes to armed (the trigger) AND Output 2 is currently armed →
                    send an alert. Output 2 doesn't need to change — it just needs to already be armed when
                    Output 1 triggers.
                </p>
                <br />
                <div style={{ display: "flex", justifyContent: "flex-end" }}>
                    <Button label="Got it" severity="success" onClick={() => setDeadlineDescriptionVisible(false)} />
                </div>
            </Dialog>
        </>
    );
};

export default WizardStepConditions;
